import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

import {
  createEvent,
  type CreateEventInput,
  type EventRow,
} from "@/lib/api/events";
import { enqueueMeetupEmail, meetupEventUrl } from "@/lib/api/meetup-emails";
import { supabase } from "@/lib/supabase";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input, Select, Textarea } from "@/components/ui/Input";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import { PageHeadline } from "@/components/ui/Headline";
import { ErrorBlock } from "@/components/ui/States";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

type ExpectedSize = "up_to_50" | "51_100" | "101_500" | "500_plus";

const SIZE_OPTIONS: { value: ExpectedSize; label: string }[] = [
  { value: "up_to_50", label: "Up to 50 (Free)" },
  { value: "51_100", label: "51–100 (Pro)" },
  { value: "101_500", label: "101–500 (Pro)" },
  { value: "500_plus", label: "500+ (Enterprise)" },
];

const REVEAL_OPTIONS = [
  { value: 48, label: "2 days before event" },
  { value: 24, label: "1 day before event" },
  { value: 12, label: "12 hours before event" },
  { value: 6, label: "6 hours before event" },
  { value: 2, label: "2 hours before event (recommended)" },
];

interface FormState {
  title: string;
  description: string;
  eventDate: string;
  location: string;
  expectedSize: ExpectedSize;
  matchRevealHours: number;
}

export function CreateEventPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<FormState>({
    title: "",
    description: "",
    eventDate: "",
    location: "",
    expectedSize: "up_to_50",
    matchRevealHours: 2,
  });
  const [createdEvent, setCreatedEvent] = useState<EventRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (
      !form.title ||
      !form.eventDate ||
      !form.location.trim() ||
      !form.description.trim()
    ) {
      setError("Please fill in event name, description, date, and venue.");
      setStep(1);
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const input: CreateEventInput = {
        title: form.title,
        description: form.description.trim(),
        event_date: new Date(form.eventDate).toISOString(),
        location: form.location.trim(),
        expected_size: form.expectedSize,
        match_reveal_hours: form.matchRevealHours,
      };
      const event = await createEvent(input);
      setCreatedEvent(event);
      setStep(3);

      const { data: userData } = await supabase.auth.getUser();
      if (userData.user?.email && event.slug) {
        void enqueueMeetupEmail({
          eventId: event.id,
          toEmail: userData.user.email,
          toName: (userData.user.user_metadata?.full_name as string) ?? undefined,
          emailType: "meetup_org_event_created",
          payload: {
            eventTitle: event.title,
            eventUrl: meetupEventUrl(event.slug),
            dashboardUrl: `https://meetup.finally.plus/events/${event.id}`,
          },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create event.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[720px] animate-fade-in">
        <Stepper current={step} />

        <Card padding="lg" className="mt-6">
          {error ? <ErrorBlock title="Could not save" message={error} className="mb-6" /> : null}

          {step === 1 && (
            <Step1
              form={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2
              form={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              onBack={() => setStep(1)}
              onNext={handleCreate}
              creating={creating}
            />
          )}
          {step === 3 && createdEvent && (
            <Step3
              event={createdEvent}
              onGoToDashboard={() => navigate(`/events/${createdEvent.id}`)}
            />
          )}
        </Card>
      </div>
    </OrganizerShell>
  );
}

function Stepper({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: "Basic data" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Invite participants" },
  ] as const;
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s.n} className="flex flex-1 items-center gap-2">
          <div
            className={
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold " +
              (current === s.n
                ? "bg-[#2563eb] text-white"
                : current > s.n
                  ? "bg-[#f0fdf4] text-[#065f46]"
                  : "bg-[#f8fafc] text-[#94a3b8]")
            }
          >
            {current > s.n ? "✓" : s.n}
          </div>
          <span
            className={
              "text-[13px] font-semibold " +
              (current >= s.n ? "text-[#1a1a1a]" : "text-[#94a3b8]")
            }
          >
            {s.label}
          </span>
          {i < steps.length - 1 ? (
            <div className="flex-1 border-t border-dashed border-[#e2e8f0]" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Step1({
  form,
  onChange,
  onNext,
}: {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  onNext: () => void;
}) {
  const canContinue =
    form.title.trim().length > 0 &&
    form.eventDate.length > 0 &&
    form.location.trim().length > 0 &&
    form.description.trim().length > 0;

  return (
    <>
      <PageHeadline
        eyebrow="Step 1 of 3"
        title="Create your event"
        subtitle="The essentials your participants will see in invitations and reminders."
      />

      <div className="mt-8 flex flex-col gap-5">
        <Field label="Event name" required>
          <Input
            value={form.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="e.g. Lisbon Founders Meetup #12"
          />
        </Field>
        <Field
          label="Description"
          required
          hint="This text is shown to participants before they record their pitch. The AI uses it to improve match quality."
        >
          <Textarea
            value={form.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="A monthly meetup for early-stage founders in Lisbon. We bring together first-time and serial founders working on B2B SaaS, fintech, and AI products. Join us to find co-founders, share lessons, and meet investors active in the local ecosystem."
            rows={4}
          />
        </Field>
        <Field label="Date & time" required>
          <DateTimePicker
            value={form.eventDate}
            onChange={(eventDate) => onChange({ eventDate })}
          />
        </Field>
        <Field
          label="Venue / address"
          required
          hint="Included in participant invitations and the day-of reminder."
        >
          <Input
            value={form.location}
            onChange={(e) => onChange({ location: e.target.value })}
            placeholder="Hub Criativo do Beato, Rua do Açúcar 83, Lisbon"
          />
        </Field>
        <Field
          label="Expected participants"
          hint="Free tier covers up to 50 — we'll prompt to upgrade if you cross the limit."
        >
          <Select
            value={form.expectedSize}
            onChange={(e) => onChange({ expectedSize: e.target.value as ExpectedSize })}
            options={SIZE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
        </Field>

        <Button onClick={onNext} disabled={!canContinue} fullWidth trailingIcon={<ArrowRight className="h-4 w-4" />}>
          Continue
        </Button>
      </div>
    </>
  );
}

function Step2({
  form,
  onChange,
  onBack,
  onNext,
  creating,
}: {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  onBack: () => void;
  onNext: () => void;
  creating: boolean;
}) {
  return (
    <>
      <PageHeadline
        eyebrow="Step 2 of 3"
        title="Create your event"
        subtitle="Decide when matches reveal and how moderation works."
      />

      <div className="mt-8 flex flex-col gap-5">
        <Field
          label="When should matches be revealed?"
          hint="A short reveal window creates anticipation and ensures people show up."
        >
          <Select
            value={form.matchRevealHours}
            onChange={(e) => onChange({ matchRevealHours: Number(e.target.value) })}
            options={REVEAL_OPTIONS}
          />
        </Field>

        <Card tone="info" padding="md">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-[#2563eb]" />
            <div>
              <p className="text-[14px] font-bold text-[#1d4ed8]">AI moderation</p>
              <p className="mt-1 text-[13px] text-[#1d4ed8]">
                Every pitch is automatically reviewed by AI. You'll only see the flagged ones — typically less than 5% of pitches.
              </p>
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            variant="secondary"
            onClick={onBack}
            leadingIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          <Button onClick={onNext} loading={creating} fullWidth>
            {creating ? "Creating event..." : "Create Event"}
          </Button>
        </div>
      </div>
    </>
  );
}

function Step3({ event, onGoToDashboard }: { event: EventRow; onGoToDashboard: () => void }) {
  const [copiedLink, setCopiedLink] = useState(false);

  const eventUrl = `https://meetup.finally.plus/e/${event.slug ?? ""}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <>
      <PageHeadline
        eyebrow="Step 3 of 3"
        title="Your event is live 🎉"
        subtitle="Share this link at the venue, on the tables, or by email."
      />

      <div className="mt-6 flex flex-col gap-5">
        <Card tone="soft" padding="md">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-[#94a3b8]">
            Share link
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-[#2563eb]">{eventUrl}</p>
          <Button variant="secondary" size="sm" onClick={copyLink} className="mt-3">
            {copiedLink ? "✓ Copied" : "Copy Link"}
          </Button>
        </Card>

        <Button onClick={onGoToDashboard} fullWidth>
          Go to Dashboard →
        </Button>
      </div>
    </>
  );
}
